import type {
  Attachment,
  CriterionKey,
  CriterionStatus,
  FileEvaluation,
  FileEvaluationCriterion,
  SessionSnapshotResponse,
} from "@nlc/shared-types";

import type {
  DiagnosticCheck,
  FileResult,
  FileResultStatus,
  FileTag,
} from "../types";

import { deriveAttachmentThumbnailUrl } from "./attachmentPreview";

/**
 * Server-side `FileEvaluationTag` is richer than the UI's three-state `FileTag`.
 * Pre-AI states (`queued` / `processing`) collapse into `pending_review` +
 * `loading` so the existing UI components don't need new branches.
 *
 * `replaced` and `discarded` are filtered out at the snapshot boundary
 * (`snapshotToFileResults` below); seeing them here is a backend bug.
 */
function mapTagAndStatus(evaluation: FileEvaluation | null): {
  status: FileResultStatus;
  tag: FileTag;
} {
  if (!evaluation) {
    return { status: "loading", tag: "pending_review" };
  }

  switch (evaluation.displayStatus) {
    case "loading": {
      return { status: "loading", tag: "pending_review" };
    }
    case "passed": {
      return { status: "done", tag: "auto_checked" };
    }
    case "needs_review": {
      return { status: "done", tag: "pending_review" };
    }
    case "overridden": {
      return { status: "done", tag: "overridden" };
    }
    case "failed": {
      return { status: "done", tag: "failed" };
    }
    default: {
      return { status: "loading", tag: "pending_review" };
    }
  }
}

const CRITERION_LABELS: Record<CriterionKey, string> = {
  brand_adherence: "Brand adherence",
  copy_compliance: "Copy compliance",
  file_type_format: "File type & format",
  size_dimensions: "Size & dimensions",
  spelling_grammar: "Spelling & grammar",
};

function mapCriterionStatus(
  status: CriterionStatus,
): DiagnosticCheck["status"] {
  return status;
}

function mapCriterion(criterion: FileEvaluationCriterion): DiagnosticCheck {
  return {
    description: criterion.message ?? "",
    id: String(criterion.id),
    status: mapCriterionStatus(criterion.status),
    title: CRITERION_LABELS[criterion.criterionKey] ?? criterion.criterionKey,
  };
}

interface MapAttachmentInput {
  attachment: Attachment;
  evaluation: FileEvaluation | null;
  /** Local blob from this browser session; absent after resume. */
  localFile: File | null;
}

export function mapAttachmentToFileResult({
  attachment,
  evaluation,
  localFile,
}: MapAttachmentInput): FileResult {
  const { status, tag } = mapTagAndStatus(evaluation);
  const checks = evaluation
    ? evaluation.criteria.map((criterion) => mapCriterion(criterion))
    : [];

  return {
    checks,
    evaluationId: evaluation?.id ?? null,
    file: localFile ?? synthesizePlaceholderFile(attachment),
    id: String(attachment.id),
    previewUrl: deriveAttachmentThumbnailUrl(attachment.url),
    remoteUrl: attachment.url,
    score: evaluation?.overallScore ?? 0,
    status,
    tag,
  };
}

/**
 * Filters out evaluations whose `tag` represents a no-longer-live row.
 * Backend snapshot already filters most of these via the JOIN, but the type
 * permits them so we guard at the boundary.
 */
export function snapshotToFileResults(
  snapshot: SessionSnapshotResponse,
  localFiles: Map<number, File>,
): FileResult[] {
  return snapshot.attachments.map((entry) =>
    mapAttachmentToFileResult({
      attachment: entry.attachment,
      evaluation: entry.evaluation,
      localFile: localFiles.get(entry.attachment.id) ?? null,
    }),
  );
}

export function isSnapshotTerminal(
  snapshot: SessionSnapshotResponse | undefined,
): boolean {
  if (!snapshot) return false;

  return snapshot.attachments.every(({ evaluation }) => {
    if (evaluation === null) return false;

    return evaluation.displayStatus !== "loading";
  });
}

/**
 * Used when we don't have the original `File` (e.g. session resume from a new
 * tab). Wraps the remote URL in a `File` shim so the existing UI components,
 * which expect a `File`, keep working without changes.
 */
export function resolveAttachmentFile(
  attachment: Attachment,
  localFiles: Map<number, File>,
): File {
  return localFiles.get(attachment.id) ?? synthesizePlaceholderFile(attachment);
}

function synthesizePlaceholderFile(attachment: Attachment): File {
  return new File([], attachment.name, {
    type: attachment.mime || "image/png",
  });
}
